import { LocalStorageKeys } from "@/components/pages/login/hooks/useLocalStorage";
import { UserProfile } from "@/services/authService";
import { Role } from "@/types/user";
import { PlatformRole } from "@/utils/accessControl";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getStringClaim(
  payload: Record<string, unknown>,
  keys: string[],
): string {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === "string" && value.length > 0) {
      return value;
    }
  }
  return "";
}

function readRoleStrings(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === "string");
}

function getRealmRolesFromPayload(payload: Record<string, unknown>): string[] {
  const realmAccess = payload["realm_access"];
  if (!isRecord(realmAccess)) {
    return [];
  }
  return readRoleStrings(realmAccess["roles"]);
}

function getClientRolesFromPayload(payload: Record<string, unknown>): string[] {
  const resourceAccess = payload["resource_access"];
  if (!isRecord(resourceAccess)) {
    return [];
  }
  const collected: string[] = [];
  for (const clientId of Object.keys(resourceAccess)) {
    const entry = resourceAccess[clientId];
    if (!isRecord(entry)) {
      continue;
    }
    collected.push(...readRoleStrings(entry["roles"]));
  }
  return collected;
}

export function decodeJwtPayload(
  accessToken: string,
): Record<string, unknown> | null {
  const parts = accessToken.split(".");
  if (parts.length !== 3) {
    return null;
  }
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padLength = (4 - (base64.length % 4)) % 4;
    const padded = base64 + "=".repeat(padLength);
    const json = atob(padded);
    const parsed: unknown = JSON.parse(json);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function isSuperAdminJwtPayload(
  payload: Record<string, unknown>,
): boolean {
  const realmRoles = getRealmRolesFromPayload(payload);
  if (realmRoles.includes(PlatformRole.SUPER_ADMIN)) {
    return true;
  }
  return getClientRolesFromPayload(payload).includes(PlatformRole.SUPER_ADMIN);
}

export function isGuesteriaAdminJwtPayload(
  payload: Record<string, unknown>,
): boolean {
  const realmRoles = getRealmRolesFromPayload(payload);
  if (realmRoles.includes(PlatformRole.GUESTERIA_ADMIN)) {
    return true;
  }
  return getClientRolesFromPayload(payload).includes(
    PlatformRole.GUESTERIA_ADMIN,
  );
}

function getPlatformAdminRoleFromPayload(
  payload: Record<string, unknown>,
): PlatformRole | null {
  if (isSuperAdminJwtPayload(payload)) {
    return PlatformRole.SUPER_ADMIN;
  }

  if (isGuesteriaAdminJwtPayload(payload)) {
    return PlatformRole.GUESTERIA_ADMIN;
  }

  return null;
}

export function isTenantAdminJwtPayload(
  payload: Record<string, unknown>,
): boolean {
  const realmRoles = getRealmRolesFromPayload(payload);
  if (realmRoles.includes(Role.TENANT_ADMIN)) {
    return true;
  }
  return getClientRolesFromPayload(payload).includes(Role.TENANT_ADMIN);
}

export function getAdminImpersonationExitPath(
  payload: Record<string, unknown>,
): string | null {
  if (isSuperAdminJwtPayload(payload) || isGuesteriaAdminJwtPayload(payload)) {
    return "/tenants";
  }

  if (isTenantAdminJwtPayload(payload)) {
    return "/users";
  }

  return null;
}

export function getImpersonatorIdFromJwtPayload(
  payload: Record<string, unknown>,
): string | null {
  const impersonatorId = getStringClaim(payload, [
    "impersonator_id",
    "impersonatorId",
  ]);
  return impersonatorId.length > 0 ? impersonatorId : null;
}

export function getTenantIdFromJwtPayload(
  payload: Record<string, unknown>,
): string | null {
  const tenantId = getStringClaim(payload, ["tenant_id", "tenantId"]);
  return tenantId.length > 0 ? tenantId : null;
}

export function getHotelIdFromJwtPayload(
  payload: Record<string, unknown>,
): string | null {
  const hotelId = getStringClaim(payload, ["hotel_id", "hotelId"]);
  return hotelId.length > 0 ? hotelId : null;
}

export function buildSuperAdminUserProfileFromJwt(
  payload: Record<string, unknown>,
  tenantIdFallback: string,
): UserProfile {
  return buildPlatformAdminUserProfileFromJwt(
    payload,
    tenantIdFallback,
    PlatformRole.SUPER_ADMIN,
  );
}

export function buildPlatformAdminUserProfileFromJwt(
  payload: Record<string, unknown>,
  tenantIdFallback: string,
  role: PlatformRole,
): UserProfile {
  const epoch = new Date(0);
  const sub = getStringClaim(payload, ["sub"]);
  const catalogUserId = getStringClaim(payload, ["userId", "user_id"]);
  const subjectId =
    sub.length > 0
      ? sub
      : getStringClaim(payload, ["preferred_username"]) || tenantIdFallback;
  const profileId = catalogUserId || subjectId;
  const tenantId = getTenantIdFromJwtPayload(payload) || tenantIdFallback;
  const firstName = getStringClaim(payload, ["given_name", "firstName"]);
  const lastName = getStringClaim(payload, ["family_name", "lastName"]);
  const email = getStringClaim(payload, ["email"]);
  const preferredLanguage =
    getStringClaim(payload, ["locale", "region"]) || "en";
  const platformPermissions =
    readRoleStrings(payload["platformPermissions"]).length > 0
      ? readRoleStrings(payload["platformPermissions"])
      : readRoleStrings(payload["platform_permissions"]);

  return {
    id: profileId,
    openIdUserId: subjectId,
    tenantId,
    role,
    ...(platformPermissions.length > 0 ? { platformPermissions } : {}),
    createdAt: epoch,
    updatedAt: epoch,
    access: null,
    profile: {
      id: profileId,
      tenantId,
      preferredLanguage,
      ...(firstName.length > 0 ? { firstName } : {}),
      ...(lastName.length > 0 ? { lastName } : {}),
      ...(email.length > 0 ? { email } : {}),
    },
  };
}

export function tryPlatformAdminProfileFromAccessToken(): UserProfile | null {
  const token = localStorage.getItem(LocalStorageKeys.ACCESS_TOKEN);
  if (!token) {
    return null;
  }
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return null;
  }
  const role = getPlatformAdminRoleFromPayload(payload);
  if (!role) {
    return null;
  }

  const tenantIdFallback =
    localStorage.getItem(LocalStorageKeys.TENANT_ID) ?? "";
  return buildPlatformAdminUserProfileFromJwt(payload, tenantIdFallback, role);
}

export function trySuperAdminProfileFromAccessToken(): UserProfile | null {
  const platformAdminProfile = tryPlatformAdminProfileFromAccessToken();
  return platformAdminProfile?.role === PlatformRole.SUPER_ADMIN
    ? platformAdminProfile
    : null;
}
